// FND-01 as amended at v1.937: **permissions belong to the product, roles belong to the tenant.**
//
// This file is the catalogue, and it is the only source of truth for it. A tenant composes roles
// out of these; it cannot invent one, because a permission with no code behind it protects nothing
// and offering to create one is offering a control that does nothing.
//
// The database mirrors this list so that granting an unknown key is a foreign-key violation rather
// than a typo nobody notices. The mirror is written from here after every migration, and a test
// asserts the two match — the same shape as the generated contract, for the same reason.
//
// **Adding one:** add it here, add the code that checks it, and add it to a role that should have
// it. A permission nothing checks is worse than no permission at all: it reads as a control.
/**
 * Deliberately short. It covers what exists today and nothing more: a permission for a capability
 * that has not been built is a promise on a screen, which CB-REQ ADO-06 prohibits for the same
 * reason it prohibits disabled controls.
 */
export const PERMISSIONS = [
    {
        key: 'people.view',
        module: 'People',
        label: 'See the people in this company and the roles they hold',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'people.manage',
        module: 'People',
        label: 'Invite people, give and take roles, remove access, and create and edit roles',
        // CB-REQ FND-01 at v1.9108: whoever can give roles can give themselves any of them, so holding
        // this is above the line, and it folds in `roles.manage`, withdrawn with migration 0150.
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'plan.manage',
        module: 'Plan',
        label: 'Choose the plan, its seats and add-ons',
        // CB-REQ FND-01 at v1.9100: the plan's prices are money.
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'plan.view',
        module: 'Plan',
        label: 'See the plan and AVenue’s invoices',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'clients.view',
        module: 'Clients',
        label: 'See clients and their contacts',
        // A client record carries no money in Phase 1 — payment terms say when an invoice issues, not
        // what anything costs (CLI-04) — so seeing one does not put anyone above the price line.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'clients.manage',
        module: 'Clients',
        label: 'Add and edit clients, their contacts and their payment terms',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'inventory.view',
        module: 'Inventory',
        label: 'See products, serial numbers, asset status and stock on hand',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'inventory.manage',
        module: 'Inventory',
        label: 'Add and edit products and serial numbers, and change an asset’s status',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'inventory.move',
        module: 'Inventory',
        label: 'Move stock between the depot and the venues that keep it',
        // CB-REQ INV-A12 at v1.955: the tenant decides which roles hold it (FND-01).
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'inventory.adjust',
        module: 'Inventory',
        label: 'Correct a bulk item’s count, saying why',
        // INV-A02: changing a count changes what the tenant owns, so it is its own permission.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'inventory.view-costs',
        module: 'Inventory',
        label: 'See what equipment cost to buy, who it came from, and what it costs to replace',
        // CB-REQ INV-A01 as restated at v1.940: cost fields are above the price line, so that being
        // below it means seeing no money at all — including the replacement cost a warehouse might
        // otherwise quote at check-in.
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'quotes.view',
        module: 'Quotes',
        label: 'See quotes, their lines and their status',
        // Seeing a quote is not seeing its money: prices on it are projected by prices.view (INV-10),
        // so the warehouse can see what is pencilled without seeing what it costs.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'quotes.manage',
        module: 'Quotes',
        label: 'Draft, price, send, revise and accept quotes',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'jobs.view',
        module: 'Jobs',
        label: 'See jobs, their lines and what is still short',
        // A job's lines carry no price (CB-REQ JOB-01 at v1.946), so there is no money here to project.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'jobs.manage',
        module: 'Jobs',
        label: 'Change a job’s delivery, PO and notes, and reserve its short lines',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'jobs.allocate',
        module: 'Jobs',
        label: 'Choose which serial numbers go out on a job',
        // CB-REQ INV-11 at v1.948: serials are decided at pick time, which is warehouse work. A unit is
        // its tag, serial and status, with no money, so this is below the price line.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'jobs.dispatch',
        module: 'Jobs',
        label: 'Book gear out and check it back in',
        // CB-REQ WHS-16 at v1.949: the warehouse's two moves. No money on either, so below the line.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'venues.view',
        module: 'Venues',
        label: 'See venues: where gear goes, and which hold stock',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'venues.manage',
        module: 'Venues',
        label: 'Add and edit venues',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'venues.instructions',
        module: 'Venues',
        label: 'Write, change and retire a venue’s standing instructions',
        // CB-REQ VEN-09 at v1.974: the rules crew read and acknowledge on every job there. Checked by the
        // database at commit as well as by the API (venue_instructions_permission).
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'crew.view',
        module: 'Crew',
        label: 'See the crew list, with phone numbers and email',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'crew.manage',
        module: 'Crew',
        label: 'Add, edit and retire crew members',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'shifts.view',
        module: 'Crew',
        label: 'See shifts on jobs and the roster',
        // CB-REQ CRW-11 at v1.960: a shift's agreed rate is a labour cost, shown only with prices.view,
        // so seeing shifts stays below the line.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'shifts.manage',
        module: 'Crew',
        label: 'Plan, publish and offer shifts, and record the answers',
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'leave.approve',
        module: 'Crew',
        label: 'Approve and decline leave',
        // CB-REQ CRW-02 at v1.9106: deciding a leave request. Checked by the database at commit as well
        // as by the API (leave_requests_rules).
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'shifts.own',
        module: 'Crew',
        label: 'See my own shifts, accept or decline them, and acknowledge them',
        // CB-REQ CRW-08 and CRW-13 at v1.964: the Crew role's one permission. The crew view carries no
        // rate or price (INV-10), so it stays below the line — which is what lets the crew list give it.
        crossesPriceLine: false,
        // FND-11 at v1.9108: crew are not seats; a crew member who holds only this is never counted.
        takesNoSeat: true,
    },
    {
        key: 'safety.manage',
        module: 'Compliance',
        label: 'Write, publish and link SWMS and risk assessments, and record sign-offs',
        // CB-REQ CMP-02 and CMP-04 at v1.967: its own permission, so a safety officer can hold it
        // without crew or job rights. A SWMS carries no money.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'documents.manage',
        module: 'Documents',
        label: 'Set the terms, logo and contact details printed on documents',
        // CB-REQ DOC-01 at v1.959: terms and a header carry no money.
        crossesPriceLine: false,
        takesNoSeat: false,
    },
    {
        key: 'invoices.view',
        module: 'Finance',
        label: 'See invoices, what they charge and whether they are paid',
        // An invoice is money through and through (CB-REQ INV-10), so seeing one is above the line.
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'invoices.manage',
        module: 'Finance',
        label: 'Issue invoices and record their payments',
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'prices.view',
        module: 'Pricing',
        label: 'See prices, rates and margins',
        // FND-08's named capability. The price line starts here.
        crossesPriceLine: true,
        takesNoSeat: false,
    },
    {
        key: 'prices.manage',
        module: 'Pricing',
        label: 'Set hire rates, price groups and rate structures',
        // Setting a price is seeing it (CB-REQ PRC-02 at v1.942).
        crossesPriceLine: true,
        takesNoSeat: false,
    },
];
/** CB-REQ FND-01 at v1.9108: what Office holds, which Owner is seeded from. */
const OFFICE_PERMISSIONS = [
    'people.view',
    'clients.view',
    'clients.manage',
    'inventory.view',
    'inventory.manage',
    'inventory.move',
    'inventory.adjust',
    'inventory.view-costs',
    'quotes.view',
    'quotes.manage',
    'jobs.view',
    'jobs.manage',
    'jobs.allocate',
    'jobs.dispatch',
    'venues.view',
    'venues.manage',
    'venues.instructions',
    'crew.view',
    'crew.manage',
    'shifts.view',
    'shifts.manage',
    'leave.approve',
    'documents.manage',
    'safety.manage',
    'invoices.view',
    'invoices.manage',
    'prices.view',
    'prices.manage',
];
export const STARTER_ROLES = [
    {
        name: 'Owner',
        // Everything Office holds, and the plan and the people: the first user provisioned gets it
        // (FND-01), and a tenant always has someone holding it (FND-11).
        permissions: [...OFFICE_PERMISSIONS, 'plan.manage', 'plan.view', 'people.manage'],
    },
    {
        name: 'Office',
        permissions: OFFICE_PERMISSIONS,
    },
    {
        name: 'Warehouse',
        // Sees who a job is for; does not decide payment terms or see prices. Keeps the stock records
        // current — adds serials, marks gear for repair — without seeing what any of it cost.
        permissions: [
            'people.view',
            'clients.view',
            'inventory.view',
            'inventory.manage',
            // Gear is shifted between the depot and the venues that keep it (INV-A12).
            'inventory.move',
            // What is pencilled, so prep can be planned; never what it costs.
            'quotes.view',
            // What is going out, when, and how it gets there.
            'jobs.view',
            // Which units go out: serials are decided at pick time (INV-11).
            'jobs.allocate',
            // Sending it and taking it back (WHS-16).
            'jobs.dispatch',
            // Where it is going (VEN-01).
            'venues.view',
            // Who is on the job, and how to reach them (CRW-01, CRW-11); never what they are paid.
            'crew.view',
            'shifts.view',
        ],
    },
    {
        name: 'Crew',
        // CB-REQ CRW-08 at v1.964, FND-08's scoped case: what the crew list gives a freelancer who signs
        // in by passcode. Their own shifts and nothing else. grant_crew_sign_in refuses this role if it
        // is ever given a permission above the price line.
        permissions: ['shifts.own'],
    },
];
export const ADD_ONS = [];
const BY_KEY = new Map(PERMISSIONS.map((p) => [p.key, p]));
export function permission(key) {
    return BY_KEY.get(key);
}
/** The keys that put a user above the price line (FND-08). Derived, never a second list. */
export const PRICE_LINE_KEYS = PERMISSIONS.filter((p) => p.crossesPriceLine).map((p) => p.key);
/**
 * What a user may do: the union of every permission on every role they hold (FND-01 — a user may
 * hold several and they add up). Immutable, and answers questions rather than exposing its set, so
 * a caller cannot accumulate permissions into it after the fact.
 */
export class PermissionSet {
    keys;
    constructor(keys) {
        this.keys = new Set(keys);
    }
    static empty() {
        return new PermissionSet([]);
    }
    has(key) {
        return this.keys.has(key);
    }
    /**
     * FND-08's price line, computed rather than stored: a stored flag drifts from the roles that
     * produce it the first time a role is edited, and the flag is what the sign-in method depends on.
     */
    get crossesPriceLine() {
        return PRICE_LINE_KEYS.some((key) => this.keys.has(key));
    }
    /** Sorted, so a response body and a test assertion do not depend on insertion order. */
    toArray() {
        return [...this.keys].sort();
    }
}
//# sourceMappingURL=catalogue.js.map